/** Monday boards this app links into. */
export const MONDAY_BOARDS = {
  OPPORTUNITIES: 1218286464,
  LEGAL_REQUESTS: 1218286410,
} as const;

/**
 * A deep link to one item on a board. The board id and the URL shape were pasted at
 * each call site, so a third copy was one paste away from drifting from the others.
 */
export function mondayItemUrl(
  boardId: number,
  itemId: number | string,
): string {
  return `https://legalbison.monday.com/boards/${boardId}/pulses/${itemId}`;
}

interface ColumnValue {
  id: string;
  text: string;
}

interface RawMondayItem {
  id: string;
  name: string;
  column_values: ColumnValue[];
}

export type MondayItemWithColumns<T extends string> = {
  readonly id: string;
  readonly name: string;
  get<K extends T>(columnId: K): string | undefined;
  getColumnMap(): Record<string, string>; // Debug helper
} & {
  readonly [K in T]: string | undefined;
};

export function createMondayItem<T extends string>(
  data: RawMondayItem,
): MondayItemWithColumns<T> {
  const columnMap = data.column_values.reduce(
    (acc, col) => {
      acc[col.id] = col.text;
      return acc;
    },
    {} as Record<string, string>,
  );

  const base = {
    id: data.id,
    name: data.name,
    get<K extends T>(columnId: K): string | undefined {
      return columnMap[columnId];
    },
    getColumnMap(): Record<string, string> {
      return { ...columnMap };
    },
  };

  return new Proxy(base, {
    get(target, prop) {
      if (prop in target) {
        return target[prop as keyof typeof target];
      }

      // Handle column access
      if (typeof prop === 'string') {
        return columnMap[prop];
      }

      return undefined;
    },
  }) as MondayItemWithColumns<T>;
}
